import React, { Dispatch, SetStateAction, useEffect, useState } from "react";

import moment from "moment";

import {
  getLocalStorageShowFieldRecommendations,
  useGetCarbonProgramOutcomes,
  useGetFarmReportingYear,
  useGetFarms,
  useGetFieldDetails,
  useGetFieldPractices,
  useGetFieldSimulations,
  useLocation,
  useNavigate,
  useTranslation,
} from "utils";

import { CarbonProgram, PracticeBundle, VIEW_TYPE } from "models";

import { Drawer, Flex, FlexPageSpin, Icon, PageSpin, RecommendationSwitch } from "components";

import { FieldDetailsTimeline } from "pages/app/field-timeline/components/FieldDetailsTimeline";
import { InterventionImpactPanel } from "pages/app/field-timeline/components/InterventionImpactPanel";

type FieldTimelineSimulationsHeaderProps = {
  className: string;
  dataTestId: string;
  showRecommendations: boolean;
  setShowRecommendations: Dispatch<SetStateAction<boolean>>;
  isFetchingCropRecommendations: boolean;
};

const FieldTimelineSimulationsHeader = ({
  className,
  dataTestId,
  showRecommendations,
  setShowRecommendations,
  isFetchingCropRecommendations,
}: FieldTimelineSimulationsHeaderProps) => {
  const { t } = useTranslation(["fieldTimeline"]);

  return (
    <div className={className} data-testid={dataTestId}>
      <Flex className="header" align="center" justify="space-between">
        <Flex align="center" justify="space-between">
          <Icon className="field-icon" dataTestId="field-icon" iconName="klim/field" size="xl" />
          <Flex align="left" vertical>
            <p className="paragraph-m-bold margin-0">{t("simulations.agnosticField")}</p>
            <p className="paragraph-s margin-0">{t("simulations.agnosticFieldSubheader")}</p>
          </Flex>
        </Flex>
        <Flex className="recommendation-simulation-toggles-container" align="center">
          <RecommendationSwitch
            showRecommendations={showRecommendations}
            setShowRecommendations={setShowRecommendations}
            isFetchingCropRecommendations={isFetchingCropRecommendations}
          />
          {/* TODO add handler to apply simulation to field */}
          {/* <Button type="primary" className="apply-to-field-button" onClick={() => {}}>
            {t("simulations.applyToField")}
          </Button> */}
        </Flex>
      </Flex>
    </div>
  );
};

export const FieldTimelineSimulationsOverlay = ({
  visible,
  setShowSimulations,
  carbonProgram,
  fieldId,
  setSimulationFieldId,
  isFetchingCropRecommendations,
}: {
  visible: boolean;
  setShowSimulations: Dispatch<SetStateAction<boolean>>;
  carbonProgram: CarbonProgram | null;
  fieldId: string;
  setSimulationFieldId?: (fieldId: string | undefined) => void;
  isFetchingCropRecommendations: boolean;
}) => {
  const { t } = useTranslation(["fieldTimeline"]);
  const navigate = useNavigate();
  const { currentFarm } = useGetFarms();
  const { data: activeFarmReportingYear, isLoading: isFarmReportingYearLoading } = useGetFarmReportingYear(currentFarm?.id || null);
  const location = useLocation();
  const isSimulationsPath = location.pathname.includes("/simulations");
  const isVisible = visible || isSimulationsPath;

  const [showRecommendations, setShowRecommendations] = useState(getLocalStorageShowFieldRecommendations() ?? true);
  const [practiceBundles, setPracticeBundles] = useState<PracticeBundle[]>();
  const [hasMissingData, setHasMissingData] = useState<boolean>(false);
  const [navigateToMissingData, setNavigateToMissingData] = useState<boolean>(false);

  const { data: fieldDetails } = useGetFieldDetails({ id: fieldId });
  const { data: fieldSimulations, isFetching: isFetchingFieldSimulations } = useGetFieldSimulations({ fieldId });

  // TODO: add simulation selector
  const simulationIds = fieldSimulations?.map((simulation) => simulation.id) || [];
  const activeSimulationId = simulationIds[0] || undefined;

  const { data: fieldPractices, isPending: isPendingFieldPractices } = useGetFieldPractices({
    fieldId,
    inView: !!activeSimulationId && isVisible,
    simulationId: activeSimulationId,
  });
  const { data: carbonProgramOutcomes, isFetching: isFetchingCarbonProgramOutcomes } = useGetCarbonProgramOutcomes({
    fieldIds: [fieldId],
    enabled: !!carbonProgram && !!activeSimulationId && isVisible,
    simulationIds,
  });

  useEffect(() => {
    if (visible && !isSimulationsPath) {
      navigate(`${location.pathname}/simulations${location.search}`);
    } else if (!visible && isSimulationsPath) {
      navigate(location.pathname.replace("/simulations", "") + location.search);
    }
  }, [visible, isSimulationsPath, navigate, location.pathname, location.search]);

  useEffect(() => {
    if (isPendingFieldPractices) return;
    setPracticeBundles(fieldPractices?.availablePracticeBundles);
  }, [isPendingFieldPractices, fieldPractices?.availablePracticeBundles]);

  const handleClose = () => {
    setShowSimulations(false);
    if (isSimulationsPath) {
      navigate(location.pathname.replace("/simulations", "") + location.search);
    }
  };

  return (
    <Drawer
      title={t("simulations.fieldName")}
      visible={isVisible}
      className="field-timeline-simulations-overlay"
      placement="bottom"
      height="calc(100vh - 2rem)"
      fullWidth
      onClose={handleClose}
      push={false}
      afterOpenChange={(open) => {
        // reset the selected AFV simulationFieldId when the simulation view closes (after the animation completes)
        if (!open && setSimulationFieldId) setSimulationFieldId(undefined);
      }}
    >
      {fieldDetails && practiceBundles && !isFetchingFieldSimulations && !!fieldSimulations?.length && activeSimulationId ? (
        <Flex className="field-timeline container" data-testid="field-timeline-page-container" justify="space-between">
          <Flex vertical className="main-container">
            <FieldTimelineSimulationsHeader
              className="field-timeline-simulations-header"
              dataTestId="field-timeline-simulations-header"
              showRecommendations={showRecommendations}
              setShowRecommendations={setShowRecommendations}
              isFetchingCropRecommendations={isFetchingCropRecommendations}
            />
            <FieldDetailsTimeline
              fieldDetails={fieldDetails}
              showRecommendations={showRecommendations}
              isEnrolled={true}
              carbonProgram={carbonProgram}
              simulationId={activeSimulationId}
              setHasMissingData={setHasMissingData}
              navigateToMissingData={navigateToMissingData}
              setNavigateToMissingData={setNavigateToMissingData}
              // for the simulations view, we don't need to check for the actual reporting year
              reportingYear={moment().year()}
            />
          </Flex>
          <Flex className="sidebar" vertical={true}>
            {isFarmReportingYearLoading ? (
              <FlexPageSpin />
            ) : (
              <>
                <InterventionImpactPanel
                  carbonProgramOutcomes={carbonProgramOutcomes ?? {}}
                  progress={isFetchingCarbonProgramOutcomes ? 0 : 100}
                  fieldSizes={{ [fieldDetails.id]: fieldDetails.size }}
                  viewType={VIEW_TYPE.SIMULATION}
                  hasMissingData={hasMissingData}
                  setNavigateToMissingData={setNavigateToMissingData}
                  practiceBundles={practiceBundles}
                  reportingYearFromProps={activeFarmReportingYear!} // here we know that activeFarmReportingYear is not null because we checked it in the if statement
                />
              </>
            )}
          </Flex>
        </Flex>
      ) : (
        <PageSpin />
      )}
    </Drawer>
  );
};
